import { useReducer, useEffect, ChangeEventHandler, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { emptyFunction } from 'utils';
import { IRelatedProduct } from 'models/BasicProduct';
import { AddItemRequest } from 'models/AddItemRequest';
import { PageState } from 'models/PageState';
import { AddItemResponse } from 'models/AddItemResponse';
import { getAccessoryDetails, getVariant } from 'services/ApiProductPageService';
import { getProductReviews, postUpVoteReview } from 'services/ApiReviewsService';
import { ToggleAddon } from 'views/ProductPage/addons';
import { addToCart } from 'components/Cart/actions';
import { REVIEWS_PAGE_SIZE } from 'views/ProductPage/reviews/themes/BGH/actions';
import { isInstallationConfigurator } from 'views/ProductPage/configurators/selector';
import { hasSelectedInstallation } from 'views/ProductPage/configurators/installation/selector';
import { reducer, OwnState, Action, setDrawerProduct } from './reducer';
import { ProductPageModel } from 'models/ProductPageModel';

interface Props {
  productId: string;
  url: string;
  isOpen: boolean;
}

const initialState: OwnState = {
  loading: false
};

export const useDrawerData = ({ productId, url, isOpen }: Props) => {
  const cdnUrl = useSelector<PageState, string>(state => state.appShell.links.cdnUrl);
  const dispatch = useDispatch();
  const [state, setState] = useReducer(reducer, initialState);
  const data = state.variant || state.product;
  const variantId = data?.product.variantId;
  const fullProductId = variantId ? `${productId}-${variantId}` : productId;

  // Get product data
  useEffect(() => {
    if (!isOpen || state.loading || !!state.product) {
      return;
    }

    setState({ type: 'setLoading' });

    getData(url, productId).then(dataRes => {
      if (dataRes.type === 'setProduct') {
        dispatch(setDrawerProduct((dataRes.product as ProductPageModel).product));
      }
      dataRes && setState(dataRes);
    });

    getReviews(productId).then(reviewRes => reviewRes && setState(reviewRes));
  }, [isOpen, state.loading, state.product, url, productId, dispatch]);

  const toggleAddon: ToggleAddon = (payload, selected) => {
    setState({
      type: 'toggleAddon',
      payload,
      selected
    });
  };

  const onVariant = useCallback(
    (variantId: string) => {
      setState({ type: 'setLoading' });
      void getVariant(productId, variantId).then(variant => {
        dispatch(setDrawerProduct(variant.product, variant.addons));
        setState({
          type: 'setVariant',
          variant
        });
      });
    },
    [dispatch, productId]
  );

  const onSelectValue = useCallback((id: string, value: string) => {
    setState({ type: 'setValue', id, value });
  }, []);

  const onReset = () => {
    setState({
      type: 'resetVariant'
    });
  };

  const loadMoreReviews = () => {
    return getReviews(productId, state.reviewsPage).then(setState);
  };

  const upvoteReview = (id: string) => {
    return postUpVoteReview(id).then(() => {
      setState({
        type: 'setReviewHelpful',
        id
      });
    });
  };

  const handleAdd = ({ id, quantity }: AddItemRequest): Promise<AddItemResponse> => {
    return dispatch(
      addToCart({
        input: {
          id,
          quantity,
          addonProducts: getAddonProducts(quantity, state.addons, state.product),
          configuratorVariables: data?.configurator?.values
        },
        btnLocation: 'slide_in'
      })
    );
  };

  const setZipCode: ChangeEventHandler<HTMLInputElement> = ({ target: { value } }) => {
    setState({
      type: 'setZipCode',
      value
    });
  };
  const isInstallationSelected = () => {
    const configurator = state.product?.configurator;
    if (!configurator || !isInstallationConfigurator(configurator)) {
      return false;
    }

    return hasSelectedInstallation(configurator);
  };
  const setInstallation = (productId: string) => {
    setState({
      type: 'setInstallation',
      id: productId
    });
  };
  const resetInstallation = () => {
    setState({
      type: 'setInstallation',
      id: undefined
    });
  };
  const toggleInstallationAddon = (id: string) => {
    setState({
      type: 'toggleInstallationAddon',
      id
    });
  };
  const resetInstallationAddons = () => {
    setState({
      type: 'toggleInstallationAddon',
      id: undefined
    });
  };
  const resetInstallationConfigurator = () => {
    resetInstallation();
    resetInstallationAddons();
  };

  return {
    data,
    cdnUrl,
    state,
    fullProductId,
    variantId,
    onVariant,
    onReset,
    onSelectValue,
    setZipCode,
    isInstallationSelected,
    setInstallation,
    toggleInstallationAddon,
    resetInstallationAddons,
    resetInstallationConfigurator,
    toggleAddon,
    handleAdd,
    loadMoreReviews,
    upvoteReview
  };
};

function getAddonProducts(
  quantity: number,
  addons?: OwnState['addons'],
  product?: OwnState['product']
) {
  const flattenedAddons = Object.values(addons || {})
    .map(value => value.products)
    .reduce((a, b) => a.concat(b), []);

  const configurator = product?.configurator || {};
  const installations = isInstallationConfigurator(configurator)
    ? configurator.installation.products
    : [];
  const installationAddons =
    isInstallationConfigurator(configurator) && configurator.addons
      ? configurator.addons.products
      : [];

  return [...flattenedAddons, ...installations, ...installationAddons]
    .filter(p => p.selected)
    .map(p => ({
      id: p.productId,
      quantity: p.quantity ? p.quantity.amount : quantity
    }));
}

function getData(url: string, productId: string) {
  const categoryPath = getCategoryPathFromUrl(url);
  return getAccessoryDetails(productId, categoryPath).then(
    product =>
      ({
        type: 'setProduct',
        product
      }) as Action
  );
}

function getReviews(productId: string, pageNumber = 1) {
  const skipMostPopularReviewId = ''; // not shown in drawer so we don't skip it
  return getProductReviews(productId, skipMostPopularReviewId, REVIEWS_PAGE_SIZE, pageNumber)
    .then(
      ({ reviews, pageNumber }) =>
        ({
          type: 'setReviews',
          reviews,
          pageNumber: pageNumber + 1
        }) as Action
    )
    .catch(emptyFunction);
}

function getCategoryPathFromUrl(url: IRelatedProduct['url']) {
  const splitUrl = url.split('/');

  return splitUrl.slice(0, splitUrl.length - 2).join('/');
}
